import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { FilesResponse } from '../../../services/file.service';
import { LayoutPreferenceService, BlogLayout } from '../../../services/layout-preference.service';
import { PostCardComponent } from '../post-card/post-card.component';
import { PostFeaturedComponent } from '../post-featured/post-featured.component';
import { PostDrawerComponent } from '../post-drawer/post-drawer.component';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { NewsletterCardComponent } from '../newsletter-card/newsletter-card.component';
import { BreadcrumbScope } from '../breadcrumb/breadcrumb.component';

type ViewportSize = 'phone' | 'tablet' | 'laptop' | 'desktop';

interface PostGroup { label: string; posts: FilesResponse[]; }

const MAX_INLINE_EXPANDS = 3;
const VALID_LAYOUTS: ReadonlyArray<BlogLayout> = ['inline', 'drawer-featured', 'drawer-grid'];
const ALL_SUBJECTS_KEY = '__all__';

@Component({
  selector: 'app-blog-feed',
  standalone: true,
  imports: [
    CommonModule,
    PostCardComponent,
    PostFeaturedComponent,
    PostDrawerComponent,
    EmptyStateComponent,
    NewsletterCardComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="feed" [attr.data-layout]="layout" [attr.data-size]="viewportSize">

      <!-- Hero: gradient banner with title, tagline, subject chips -->
      <section class="hero">
        <div class="hero__inner">
          <span class="hero__eyebrow">Visual study material</span>
          <h1 class="hero__title">Mindmaps for UPSC</h1>
          <p class="hero__subtitle">
            Curated, high-yield mindmaps and study notes for IAS aspirants —
            covering Polity, History, Geography, Science, and Economy.
          </p>
          <div class="hero__chips" role="tablist" aria-label="Filter by subject">
            <button type="button"
                    class="chip"
                    [class.chip--active]="activeSubject === '${ALL_SUBJECTS_KEY}'"
                    (click)="selectSubject('${ALL_SUBJECTS_KEY}')">
              All <span class="chip__count">{{ filtered.length }}</span>
            </button>
            <button *ngFor="let s of subjects; trackBy: trackSubject"
                    type="button"
                    class="chip"
                    [class.chip--active]="activeSubject === s.name"
                    (click)="selectSubject(s.name)">
              {{ s.name }} <span class="chip__count">{{ s.count }}</span>
            </button>
          </div>
        </div>
      </section>

      <main class="feed__main">
        <ng-container *ngIf="filtered.length; else emptyTpl">

          <!-- Layouts A & B share: cover-story featured + grouped feed -->
          <ng-container *ngIf="layout !== 'drawer-grid'">
            <app-post-featured
              *ngIf="featured"
              [bundle]="featured"
              [lang]="getLang(featured.id)"
              [viewportSize]="viewportSize"
              density="cover"
              (crumbNavigate)="onCrumb($event)"
              (langChange)="onLangChange($event)">
            </app-post-featured>

            <ng-container *ngFor="let group of groupedRest; trackBy: trackGroup">
              <h2 class="feed__sectionTitle">{{ group.label }}</h2>
              <div class="feed__list">
                <ng-container *ngFor="let b of group.posts; trackBy: trackById">
                  <app-post-card
                    [bundle]="b"
                    [isActive]="layout === 'inline' ? isExpanded(b.id) : (drawerBundleId === b.id)"
                    variant="changelog"
                    (activate)="onCardClick($event)"
                    (crumbNavigate)="onCrumb($event)">
                  </app-post-card>
                  <app-post-featured
                    *ngIf="layout === 'inline' && isExpanded(b.id)"
                    [bundle]="b"
                    [lang]="getLang(b.id)"
                    [viewportSize]="viewportSize"
                    density="compact"
                    [collapsible]="true"
                    (collapse)="toggleExpand(b.id)"
                    (crumbNavigate)="onCrumb($event)"
                    (langChange)="onLangChange($event)">
                  </app-post-featured>
                </ng-container>
              </div>
            </ng-container>
          </ng-container>

          <!-- Layout C: card grid (featured spans 2 cols) -->
          <ng-container *ngIf="layout === 'drawer-grid'">
            <div class="feed__grid">
              <app-post-card
                *ngFor="let b of allByDate; trackBy: trackById; let i = index"
                [bundle]="b"
                [isActive]="drawerBundleId === b.id"
                variant="grid"
                [class.gridcell--featured]="i === 0"
                (activate)="onCardClick($event)"
                (crumbNavigate)="onCrumb($event)">
              </app-post-card>
            </div>
          </ng-container>

        </ng-container>

        <ng-template #emptyTpl>
          <app-empty-state [kind]="loading ? 'loading' : 'empty'"></app-empty-state>
        </ng-template>
      </main>

      <aside class="feed__rightRail" *ngIf="showRightRail()">
        <app-newsletter-card></app-newsletter-card>
      </aside>
    </div>

    <app-post-drawer
      [bundle]="drawerBundle"
      [open]="!!drawerBundle"
      [viewportSize]="viewportSize"
      [lang]="drawerBundle ? getLang(drawerBundle.id) : 'en'"
      (close)="closeDrawer()"
      (crumbNavigate)="onCrumb($event)"
      (langChange)="onLangChange($event)">
    </app-post-drawer>
  `,
  styleUrls: ['./blog-feed.component.css'],
})
export class BlogFeedComponent implements OnInit, OnChanges, OnDestroy {
  @Input() bundles: FilesResponse[] = [];
  @Input() loading = false;

  @Output() crumbNavigate = new EventEmitter<{ scope: BreadcrumbScope; bundle: FilesResponse }>();

  layout: BlogLayout = 'inline';
  viewportSize: ViewportSize = this.detectSize();

  drawerBundleId: number | null = null;
  activeSubject: string = ALL_SUBJECTS_KEY;

  private layoutSub?: Subscription;
  private routeSub?: Subscription;
  private expandedIds: number[] = [];
  private langPerBundle = new Map<number, 'en' | 'hi'>();

  private layoutPrefs = inject(LayoutPreferenceService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.layoutSub = this.layoutPrefs.layout$.subscribe(l => {
      this.layout = l;
      if (l === 'inline' && this.drawerBundleId !== null) {
        this.drawerBundleId = null;
        this.syncQueryParam('post', null);
      }
      this.cdr.markForCheck();
    });
    this.routeSub = this.route.queryParamMap.subscribe(p => {
      const post = p.get('post');
      const id = post ? Number(post) : null;
      this.drawerBundleId = id && Number.isFinite(id) ? id : null;
      const layoutQ = p.get('layout');
      if (layoutQ && VALID_LAYOUTS.includes(layoutQ as BlogLayout) && layoutQ !== this.layout) {
        this.layoutPrefs.set(layoutQ as BlogLayout);
      }
      const subj = p.get('subject');
      if (subj) this.activeSubject = subj;
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(_: SimpleChanges): void { /* getters recompute */ }

  ngOnDestroy(): void {
    this.layoutSub?.unsubscribe();
    this.routeSub?.unsubscribe();
  }

  @HostListener('window:resize')
  onResize(): void {
    const next = this.detectSize();
    if (next !== this.viewportSize) {
      this.viewportSize = next;
      this.cdr.markForCheck();
    }
  }

  // ===== Filtered view of bundles =====
  get filtered(): FilesResponse[] {
    if (this.activeSubject === ALL_SUBJECTS_KEY) return this.bundles;
    return this.bundles.filter(b => (b.subject ?? '').toLowerCase() === this.activeSubject.toLowerCase());
  }

  get allByDate(): FilesResponse[] {
    return [...this.filtered].sort(BlogFeedComponent.byDateDesc);
  }

  get featured(): FilesResponse | null {
    return this.allByDate[0] ?? null;
  }

  get rest(): FilesResponse[] {
    const f = this.featured;
    return f ? this.allByDate.filter(b => b.id !== f.id) : [];
  }

  get groupedRest(): PostGroup[] {
    const now = Date.now();
    const week = 7 * 24 * 60 * 60 * 1000;
    const month = 30 * 24 * 60 * 60 * 1000;
    const buckets: { [k: string]: PostGroup } = {
      week:    { label: 'This week',  posts: [] },
      month:   { label: 'This month', posts: [] },
      earlier: { label: 'Earlier',    posts: [] },
    };
    for (const b of this.rest) {
      const d = b.files?.['html']?.created_at ?? b.files?.['pdf']?.created_at ?? null;
      const t = d ? new Date(d).getTime() : 0;
      const age = t ? now - t : Number.MAX_SAFE_INTEGER;
      if (age <= week) buckets['week'].posts.push(b);
      else if (age <= month) buckets['month'].posts.push(b);
      else buckets['earlier'].posts.push(b);
    }
    return Object.values(buckets).filter(g => g.posts.length);
  }

  get subjects(): Array<{ name: string; count: number }> {
    const map = new Map<string, number>();
    for (const b of this.bundles) {
      const s = (b.subject ?? '').trim();
      if (!s) continue;
      map.set(s, (map.get(s) ?? 0) + 1);
    }
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }

  get drawerBundle(): FilesResponse | null {
    if (this.layout === 'inline' || this.drawerBundleId == null) return null;
    return this.bundles.find(b => b.id === this.drawerBundleId) ?? null;
  }

  // ===== Interactions =====
  selectSubject(name: string): void {
    this.activeSubject = name;
    this.syncQueryParam('subject', name === ALL_SUBJECTS_KEY ? null : name);
    this.cdr.markForCheck();
  }

  isExpanded(id: number): boolean {
    return this.expandedIds.includes(id);
  }

  toggleExpand(id: number): void {
    if (this.expandedIds.includes(id)) {
      this.expandedIds = this.expandedIds.filter(x => x !== id);
    } else {
      this.expandedIds = [...this.expandedIds, id];
      while (this.expandedIds.length > MAX_INLINE_EXPANDS) this.expandedIds.shift();
    }
    this.cdr.markForCheck();
  }

  onCardClick(id: number): void {
    if (this.layout === 'inline') this.toggleExpand(id);
    else this.openDrawer(id);
  }

  openDrawer(id: number): void {
    this.drawerBundleId = id;
    this.syncQueryParam('post', String(id));
    this.cdr.markForCheck();
  }

  closeDrawer(): void {
    this.drawerBundleId = null;
    this.syncQueryParam('post', null);
    this.cdr.markForCheck();
  }

  getLang(id: number): 'en' | 'hi' {
    return this.langPerBundle.get(id) ?? 'en';
  }

  onLangChange(evt: { bundleId: number; lang: 'en' | 'hi' }): void {
    if (evt.lang === 'en') this.langPerBundle.delete(evt.bundleId);
    else this.langPerBundle.set(evt.bundleId, evt.lang);
    this.langPerBundle = new Map(this.langPerBundle);
    this.cdr.markForCheck();
  }

  onCrumb(evt: { scope: BreadcrumbScope; bundle: FilesResponse }): void {
    this.crumbNavigate.emit(evt);
  }

  showRightRail(): boolean {
    return this.layout === 'inline' && (this.viewportSize === 'laptop' || this.viewportSize === 'desktop');
  }

  trackById = (_: number, b: FilesResponse): number => b.id;
  trackSubject = (_: number, s: { name: string }): string => s.name;
  trackGroup   = (_: number, g: PostGroup): string => g.label;

  private syncQueryParam(key: string, value: string | null): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [key]: value },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private detectSize(): ViewportSize {
    if (typeof window === 'undefined') return 'laptop';
    const w = window.innerWidth;
    if (w <= 480) return 'phone';
    if (w <= 1024) return 'tablet';
    if (w <= 1600) return 'laptop';
    return 'desktop';
  }

  private static byDateDesc(a: FilesResponse, b: FilesResponse): number {
    const ad = a.files?.['html']?.created_at ?? a.files?.['pdf']?.created_at ?? '';
    const bd = b.files?.['html']?.created_at ?? b.files?.['pdf']?.created_at ?? '';
    if (ad && bd) return ad < bd ? 1 : ad > bd ? -1 : 0;
    return b.id - a.id;
  }
}
